import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  Row,
  Col,
  Card,
  Space,
  Button,
  Image,
  Modal,
  notification,
} from "antd";
import { Table } from "ant-table-extensions";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useMediaQuery } from "react-responsive";

import Create from "./Create";
import Edit from "./Edit";
import Ordering from "./Ordering";

import axios from "axios";
import urldata from "../../../../urldata";
import config from "../../../../config/config";

const { confirm } = Modal;

function Index() {
  const isMobile = useMediaQuery({ query: "(max-width: 576px)" });

  const [btnSize, setBtnSize] = useState("middle");
  const [datas, setDatas] = useState([]);
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState(1);

  const [editOpen, setEditOpen] = useState(false);
  const [editKey, setEditKey] = useState(1);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [sectionId, setSectionId] = useState(null);
  const [orderOpen, setOrderOpen] = useState(false);

  const moduleName = "Lifetime Memories";
  const moduleNamePlural = "Lifetime Memories";

  const base_api = urldata + "admin/";
  const getEndpoint = base_api + "getHosLife";
  const azure_base = config.azure_object_url || "";

  // ---------------------------
  // Fetch Data
  // ---------------------------
  const fetchDatas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(getEndpoint);
      const sectionData = response?.data?.data?.[0];

      setDatas(sectionData?.items || []);
      setSectionId(sectionData?._id || null);

      if (!sectionData?.items?.length) {
        notification.info({ message: "No items found" });
      }
    } catch (err) {
      console.error(err);
      notification.error({ message: "Failed to fetch data" });
      setDatas([]);
      setSectionId(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDatas();
  }, []);

  useEffect(() => {
    setBtnSize(isMobile ? "small" : "middle");
  }, [isMobile]);

  // ---------------------------
  // Create Handlers
  // ---------------------------
  const handleCreateOpen = () => setCreateOpen(true);

  const handleCreateClose = () => {
    setCreateKey((k) => k + 1);
    setCreateOpen(false);
    fetchDatas();
  };

  // ---------------------------
  // Edit Handlers
  // ---------------------------
  const handleEditOpen = (item) => {
    setSelectedRecord({ ...item, sectionId });
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditKey((k) => k + 1);
    setSelectedRecord(null);
    setEditOpen(false);
    fetchDatas();
  };

  // ---------------------------
  // Delete Handler
  // ---------------------------
  const handleDelete = (itemId) => {
    confirm({
      title: `Are you sure you want to delete this ${moduleName}?`,
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      async onOk() {
        try {
          await axios.post(base_api + "deleteHosLife", {
            itemId: itemId,
            sectionId: sectionId,
          });

          notification.success({
            message: `${moduleName} deleted successfully`,
          });
          fetchDatas();
        } catch (err) {
          console.error(err);
          notification.error({ message: "Failed to delete" });
        }
      },
    });
  };

  // ---------------------------
  // Table Columns
  // ---------------------------
  const columns = [
    {
      title: "Alt Text",
      dataIndex: "thumb",
      key: "thumb",
      width: 150,
      render: (text) => text || "-",
    },
    {
      title: "Desktop Image",
      key: "mainImg",
      render: (_, record) => (
        <Image
          width={100}
          src={azure_base + record.mainImg}
          fallback="https://via.placeholder.com/100"
        />
      ),
    },
    {
      title: "Mobile Image",
      key: "mbMainImg",
      render: (_, record) => (
        <Image
          width={100}
          src={azure_base + record.mbMainImg}
          fallback="https://via.placeholder.com/100"
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            size={btnSize}
            onClick={() => handleEditOpen(record)}
          >
            Edit
          </Button>

          <Button
            type="danger"
            size={btnSize}
            onClick={() => handleDelete(record._id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Breadcrumb style={{ margin: "16px 0" }}>
        <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>{moduleNamePlural}</Breadcrumb.Item>
      </Breadcrumb>

      <Row>
        <Col span={24}>
          <Card title={moduleNamePlural} bordered={false}>
            <Row style={{ marginBottom: 20 }}>
              <Col span={24}>
                <Space>
                  <Button
                    type="primary"
                    size={btnSize}
                    onClick={handleCreateOpen}
                  >
                    Add {moduleName}
                  </Button>

                  <Button
                    type="primary"
                    size={btnSize}
                    onClick={() => setOrderOpen(true)}
                  >
                    View Ordering
                  </Button>
                </Space>
              </Col>
            </Row>

            <Table
              loading={loading}
              rowKey={(record) => record._id}
              columns={columns}
              dataSource={datas}
              footer={() => `Total ${moduleNamePlural} = ${datas.length}`}
            />

            {/* CREATE */}
            <Create
              key={"create" + createKey}
              isVisible={createOpen}
              handleClose={handleCreateClose}
              moduleName={moduleName}
              base_url={base_api}
            />

            {/* EDIT */}
            {selectedRecord && (
              <Edit
                key={"edit" + editKey}
                isVisible={editOpen}
                handleClose={handleEditClose}
                moduleName={moduleName}
                base_url={base_api}
                record={selectedRecord}
                azure_base={azure_base}
              />
            )}

            <Ordering
              isOpen={orderOpen}
              onClose={(updatedData) => {
                setOrderOpen(false);
                if (updatedData) setDatas(updatedData); // update table immediately
              }}
              datas={datas}
              moduleName={moduleName}
              isMobile={isMobile}
              image_url={azure_base}
              base_url={base_api}
              sectionId={sectionId}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Index;
